import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Truck, AlertTriangle } from 'lucide-react';
import { DropdownItemOption } from './ItemDropdown';
import { ItemDropdown } from './ItemDropdown';
import { tg } from '../../telegram';
import { ConversionItem } from '../../types';
import { formatQty } from '../../utils/format';
import { toComparisonKey } from '../../utils/textNormalizer';

interface AddTransferItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredients: DropdownItemOption[];
  centralItems: { ingredient_name: string; quantity: number; unit?: string }[];
  units?: string[];
  conversions?: ConversionItem[];
  getInwardUnitsForItem?: (name: string) => string[];
  onAdd: (item: {
    ingredient_name: string;
    quantity: number;
    unit: string;
    max_available: number;
  }) => void;
}

export const AddTransferItemModal: React.FC<AddTransferItemModalProps> = ({
  isOpen,
  onClose,
  ingredients,
  centralItems,
  units: _units,
  conversions = [],
  getInwardUnitsForItem: _getInwardUnitsForItem,
  onAdd,
}) => {
  const [selectedName, setSelectedName] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [unit, setUnit] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedName('');
      setQuantity(1);
      setUnit('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const cleanSelectedKey = toComparisonKey(selectedName);
  const cleanSelectedName = selectedName.trim().toLowerCase();

  const matchCentral = centralItems.find(
    (c) => toComparisonKey(c.ingredient_name) === cleanSelectedKey || c.ingredient_name.toLowerCase() === cleanSelectedName
  );
  const centralStandardQty = matchCentral?.quantity || 0;
  const standardUnit = matchCentral?.unit || 'g';

  const itemConversions = conversions.filter(
    (c) => toComparisonKey(c.ingredient_name) === cleanSelectedKey || c.ingredient_name.trim().toLowerCase() === cleanSelectedName
  );
  const cleanUnit = (unit || '').trim().toLowerCase();
  const matchedConv = itemConversions.find(
    (c) => c.package_unit.trim().toLowerCase() === cleanUnit
  );

  const cleanStdUnit = standardUnit.trim().toLowerCase();
  let ratio = 1;
  let isConverted = false;
  if (matchedConv && matchedConv.ratio > 0) {
    ratio = matchedConv.ratio;
    isConverted = true;
  } else if (cleanUnit && cleanUnit === cleanStdUnit) {
    ratio = 1;
    isConverted = true;
  } else if (cleanUnit === 'kg' && cleanStdUnit === 'g') {
    ratio = 1000;
    isConverted = true;
  } else if ((cleanUnit === 'l' || cleanUnit === 'lit' || cleanUnit === 'lít') && cleanStdUnit === 'ml') {
    ratio = 1000;
    isConverted = true;
  } else {
    ratio = 0;
    isConverted = false;
  }

  const maxAvailable = isConverted ? (ratio > 1 ? Math.floor(centralStandardQty / ratio) : centralStandardQty) : 0;
  const currentQty = Math.max(0, quantity);
  const isUnconverted = Boolean(selectedName.trim() && !isConverted);
  const isValid = Boolean(selectedName.trim() && currentQty > 0 && isConverted);
  const isOverStock = maxAvailable > 0 && currentQty > maxAvailable;

  const handleSelectIngredient = (name: string, defaultUnit?: string, option?: DropdownItemOption) => {
    setSelectedName(name);
    const targetUnit = option?.package_unit || defaultUnit;
    const cleanKey = toComparisonKey(name);
    const cleanRaw = name.trim().toLowerCase();

    const itemConvs = conversions.filter(
      (c) => toComparisonKey(c.ingredient_name) === cleanKey || c.ingredient_name.trim().toLowerCase() === cleanRaw
    );
    const matched = centralItems.find(
      (c) => toComparisonKey(c.ingredient_name) === cleanKey || c.ingredient_name.toLowerCase() === cleanRaw
    );
    const meta = ingredients.find(
      (i) => toComparisonKey(i.name) === cleanKey || i.name.toLowerCase() === cleanRaw
    );

    // Prioritize packaging unit from option or conversions (Luồng 2 ưu tiên xuất theo bao bì)
    let resolvedUnit = targetUnit ? targetUnit.trim() : '';
    if (!resolvedUnit || ((resolvedUnit.toLowerCase() === 'g' || resolvedUnit.toLowerCase() === 'ml') && itemConvs.length > 0)) {
      resolvedUnit =
        itemConvs.length > 0
          ? itemConvs[0].package_unit
          : matched?.unit || meta?.unit || 'g';
    }
    setUnit(resolvedUnit);
    setQuantity(1);
  };

  const handleConfirmAdd = () => {
    if (!isValid) return;
    tg.haptic.success();
    onAdd({
      ingredient_name: selectedName.trim(),
      quantity: currentQty,
      unit: unit.trim() || 'g',
      max_available: maxAvailable,
    });
    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--primary-accent-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-accent)',
              }}
            >
              <Truck size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                Thêm Món Chuyển Kho
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Xuất nguyên liệu từ Kho Tổng sang Quầy Bar
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32, padding: 0, color: 'var(--text-secondary)' }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* 1. Chọn nguyên liệu */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            1. Chọn nguyên liệu cần xuất (*)
          </label>
          <ItemDropdown
            value={selectedName}
            selectedUnit={unit}
            displayMode="name_with_unit"
            items={ingredients}
            placeholder="🔍 Bấm để tìm & chọn nguyên liệu..."
            onChange={handleSelectIngredient}
            style={{ minHeight: 44, height: 44, fontSize: 14 }}
          />

          {selectedName && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, paddingLeft: 2 }}>
              Tồn Kho Tổng: <b style={{ color: 'var(--primary-accent)' }}>{formatQty(maxAvailable, unit)}</b>
              {ratio > 1 && (
                <span style={{ color: 'var(--text-tertiary)', marginLeft: 4 }}>
                  {`(~${formatQty(centralStandardQty, standardUnit)})`}
                </span>
              )}
            </div>
          )}
        </div>

        {/* 2. Số lượng xuất */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            2. Số lượng xuất {unit ? `(${unit})` : ''} (*)
          </label>
          {/* Stepper Full Width */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1.5px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--bg-surface)',
              overflow: 'hidden',
              width: '100%',
              minHeight: 48,
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={() => handleQuickAddQty(-1)}
              className="stepper-btn"
              style={{ width: 50, height: 48, fontSize: 22 }}
              title="Giảm 1"
            >
              <Minus size={20} />
            </button>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <input
                type="number"
                min="0.1"
                step="any"
                className="num"
                value={quantity === 0 ? '' : quantity}
                onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                onBlur={(e) => {
                  if (!e.target.value) setQuantity(1);
                }}
                style={{
                  width: 70,
                  border: 'none',
                  outline: 'none',
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 20,
                  color: 'var(--text-primary)',
                  backgroundColor: 'transparent',
                  padding: '4px',
                }}
              />
              {unit && (
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-secondary)' }}>
                  {unit}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleQuickAddQty(1)}
              className="stepper-btn"
              style={{ width: 50, height: 48, fontSize: 22 }}
              title="Tăng 1"
            >
              <Plus size={20} />
            </button>
          </div>

          {/* Quick presets */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 5, 10, 20, 50].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity(preset);
                }}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)',
                  backgroundColor: quantity === preset ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                  color: quantity === preset ? '#FFFFFF' : 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>

        {/* Warning if unit lacks conversion rule */}
        {isUnconverted && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--color-danger)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--color-danger)',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span>
              Chưa có quy tắc quy đổi từ "{unit}" sang "{standardUnit}". Vui lòng thêm vào tab 04_Quy_doi!
            </span>
          </div>
        )}

        {/* Warning if over stock */}
        {isOverStock && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-warning-bg)',
              border: '1px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: 'var(--color-warning)',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
            <span>
              Số lượng xuất ({currentQty}) đang lớn hơn số lượng tồn Kho Tổng ({maxAvailable} {unit}).
            </span>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={!isValid}
            onClick={handleConfirmAdd}
            className="btn btn-primary btn-full"
            style={{
              minHeight: 48,
              fontSize: 15,
              fontWeight: 700,
              gap: 8,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm {currentQty > 0 ? `${currentQty} ${unit || ''}`.trim() : ''} Vào Danh Sách
          </button>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-full"
            style={{ minHeight: 42, fontSize: 13, fontWeight: 600 }}
          >
            Hủy Bỏ
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
